<script>
  import { onMount, tick } from 'svelte';
  import { createEventDispatcher } from 'svelte';
  import autosize from 'autosize';

  const dispatch = createEventDispatcher();

  export let label;
  export let name;
  export let value = '';
  export let error = null;
  export let placeholder = null;
  export let loading = false;
  export let rows = 1;
  export let autogrow = false;
  export let preventNewLineOnEnter = false;
  export let preventTab = false;
  export let inputEl = null;
  export let focus = false;
  export let showCharCount = false;

  let previousValue = '';

  let setFocus = async function (node) {
    if (focus) {
      await tick();
      node.focus();
    }
  };

  let handleKeyDown = function (event) {
    let which = event.which || event.keyCode || 0;

    if (preventTab && which == 9) {
      dispatch('tab', {
        event: event,
      });

      event.preventDefault();
    }

    if (preventNewLineOnEnter && which == 13 && !event.shiftKey) {
      event.preventDefault();
    }

    dispatch('keydown', {
      event: event,
      which: event.which || event.keyCode || 0,
      value: event.target.value,
    });
  };

  let handleBlur = function (event) {
    value = event.target.value;

    if (value != previousValue) {
      dispatch('change', {
        value: event.target.value,
      });
    }

    dispatch('blur', {
      event: event,
      value: event.target.value,
    });
  };

  let handleKeyUp = function (event) {
    dispatch('keyup', {
      event: event,
      which: event.which || event.keyCode || 0,
      value: event.target.value,
    });

    if (value != previousValue) {
      dispatch('change', {
        value: event.target.value,
      });
    }

    previousValue = event.target.value;
  };

  let setupAutogrow = function (node) {
    if (autogrow) {
      autosize(node);

      let totalRuns = 0;
      let int = setInterval(() => {
        if (totalRuns >= 20 || (value && value != '')) {
          autosize.update(node);
          clearInterval(int);
        }

        totalRuns = totalRuns + 1;
      }, 50);
    }
  };

  onMount(() => {
    dispatch('mounted', { input: inputEl });
  });
</script>

<style style="text/scss" lang="scss">
  .input-container {
    position: relative;
    display: flex;
    justify-content: center;
    flex-direction: column;

    .input-loading-spinner {
      position: absolute;
      right: 20px;
      top: 16px;
      padding-left: 15px;
      z-index: 1;
      opacity: 0.75;
    }

    label {
      font-size: 15px;
      line-height: 15px;
      opacity: 0.5;
      margin-bottom: 12px;
      font-weight: normal;
    }

    textarea {
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;

      transition:
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out;
      border-radius: 15px;
      border: 2px solid #ececec;
      padding-left: 21px;
      padding-right: 21px;
      padding-top: 12px;
      padding-bottom: 12px;
      min-height: 50px;
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
      color: #495057;
      width: 100%;
      resize: none;

      &.with-char-count {
        padding-bottom: 20px;
      }

      &::placeholder {
        opacity: 0.85;
        font-weight: 400;
      }

      &:focus {
        background-color: #f6f4fe;
        border-color: #a893f9;
        outline: 0;
        outline: none;
      }

      &:active,
      &:hover {
        outline: none;
      }
    }

    .input-error-msg {
      width: 100%;
      margin-top: 0.25rem;
      font-size: 80%;
      color: #dc3545;
    }

    .input-error {
      border: 2px solid #dc3545 !important;
      padding-right: calc(40px) !important;
      background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='%23dc3545' viewBox='-2 -2 7 7'%3e%3cpath stroke='%23dc3545' d='M0 0l3 3m0-3L0 3'/%3e%3ccircle r='.5'/%3e%3ccircle cx='3' r='.5'/%3e%3ccircle cy='3' r='.5'/%3e%3ccircle cx='3' cy='3' r='.5'/%3e%3c/svg%3E");
      background-repeat: no-repeat;
      background-position: center right calc(15px);
      background-size: calc(18px) calc(18px);
    }

    .loading-input {
      padding-right: 50px !important;
    }

    .char-count {
      color: #b3b3b3;
      position: absolute;
      bottom: 16px;
      right: 16px;
      font-size: 14px;
    }
  }

  .autogrow {
    resize: none;
  }
</style>

<div class="input-container {$$props.class}" style={$$props.inputContainerStyle}>
  {#if label}
    <label for="" class={$$props.labelClass}>
      {@html label}
    </label>
  {/if}

  <div style="position: relative">
    {#if loading}
      <div class="input-loading-spinner {$$props.loadingClass}">
        <i class="fa fa-spin fa-spinner {$$props.loadingSpinnerClass}" />
      </div>
    {/if}

    <textarea
      use:setFocus
      use:setupAutogrow
      class:autogrow={autogrow}
      {...$$props.inputProps}
      style={$$props.textareaStyle}
      maxlength={$$props.maxlength}
      bind:this={inputEl}
      class:loading-input={loading}
      class:input-error={error}
      on:click={(e) => {
        dispatch('click', { event: e });
      }}
      on:blur={handleBlur}
      on:focus={(e) => {
        dispatch('focus', { event: e });
      }}
      on:keydown={handleKeyDown}
      on:keyup={handleKeyUp}
      on:paste={() => dispatch('paste')}
      class={$$props.textareaClass}
      class:with-char-count={showCharCount}
      name={name}
      placeholder={placeholder}
      rows={rows}
      bind:value={value} />

    {#if $$props.maxlength && showCharCount}
      <div class="char-count">{value.length} / {$$props.maxlength}</div>
    {/if}
  </div>
  {#if error}
    <div class="input-error-msg {$$props.errorClass}">{error}</div>
  {/if}
</div>
